import React from "react";
import { useTranslation } from "react-i18next";
import { GripVertical, Edit, Trash2 } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Stage } from "../../types/shared.types";
import type { SortableStageItemProps } from "../../types/admin.types";

// Extended Stage type with code field
interface AdminStage extends Stage {
  code?: string;
}

/**
 * SortableStageItem Component
 * Renders a draggable stage item for the Kanban columns management
 */
const SortableStageItem: React.FC<SortableStageItemProps> = ({
  stage,
  onEdit,
  onDelete,
  usageCount = 0,
}) => {
  const { t } = useTranslation(["admin", "common"]);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="stage-item">
      <div className="stage-item-content">
        <div className="stage-drag-handle" {...attributes} {...listeners}>
          <GripVertical size={16} />
        </div>
        <div
          className="stage-color-indicator"
          style={{ backgroundColor: stage.color || "#3b82f6" }}
        />
        <div className="stage-info">
          <div className="stage-name">{stage.name}</div>
          <div className="stage-code">{(stage as AdminStage).code}</div>
          {usageCount > 0 && (
            <div className="stage-usage">
              {t("admin:stages.usedByCount", { count: usageCount })}
            </div>
          )}
        </div>
        <div className="stage-actions">
          <button
            className="btn-sm btn-outline"
            onClick={() => onEdit(stage)}
            title={t("common:actions.edit")}
          >
            <Edit size={16} />
          </button>
          <button
            className="btn-sm btn-delete"
            onClick={() => onDelete(stage.id)}
            title={t("common:actions.delete")}
            disabled={usageCount > 0}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SortableStageItem;
